import { useCallback, useRef } from 'react';
import type { LanguageProperty } from 'ehs-react';
import { DefaultDateFormatter } from 'ehs-react';
import { intl } from 'ehs-react';

export function useInfiniteScroll(
  callback: () => void
): (node: HTMLElement | null) => void {
  const observer = useRef<IntersectionObserver | null>(null);

  return useCallback(
    (node: HTMLElement | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          callback();
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [callback]
  );
}

export const notificationTypesConfig: { [key: string]: any } = {
  EMERGENCY_ACCESS: {
    titleKey: 'view.patient.detail.notifications.emergencynotifications',
    icon: 'ehealthicon-ambulance'
  },
  NEW_HCP_IN_GROUP: {
    titleKey: 'view.patient.detail.notifications.groupchanges',
    icon: 'ehealthicon-user-groups'
  },
  NEW_DOCUMENT_RECEIVED: {
    titleKey: 'view.patient.detail.notifications.newdocumentreceived',
    icon: 'ehealthicon-file-text'
  },
  /** Represents the "Document or Image received" topic type. */
  DOCUMENT_TRANSFER: {
    titleKey:
      'portalsetupparameters.sense_physician.referreddocuments_layout.name',
    icon: 'ehealthicon-folder-with-a-male-avatar'
  },
  /** Represents the "Transferral received" topic type. */
  TRANSFERRAL_RECEIVED: {
    titleKey: 'view.physician.notifications.transferral.received',
    icon: 'ehealthicon-resume'
  },
  /** Represents the "Transferral: New comment added" topic type. */
  TRANSFERRAL_COMMENT: {
    titleKey: 'view.physician.notifications.transferral.comment',
    icon: 'ehealthicon-resume'
  },
  /** Represents the "Transferral: Status changed" topic type. */
  TRANSFERRAL_STATUS: {
    titleKey: 'view.physician.notifications.transferral.status',
    icon: 'ehealthicon-resume'
  },
  /** Represents the "Transferral: Documents added" topic type. */
  TRANSFERRAL_DOCUMENT: {
    titleKey: 'view.physician.notifications.transferral.document',
    icon: 'ehealthicon-resume'
  },
  /** Represents the "Appointment: Reminder upcoming appointment" topic type */
  APPOINTMENT_REMINDER: {
    titleKey: 'view.patient.detail.notifications.appointmentreminder',
    icon: 'ehealthicon-appointment_notification'
  },
  /** Represents the "Appointment: Appointment created or updated" topic type. */
  APPOINTMENT_UPDATE: {
    titleKey: 'view.patient.detail.notifications.appointmentupdate',
    icon: 'ehealthicon-appointment_update'
  },
  USER_ACCOUNT_DEACTIVATION: {
    titleKey: 'view.physician.notifications.user.account.deactivation',
    icon: 'ehealthicon-account_deactivation'
  },
  MESSAGE: {
    titleKey: 'view.physician.notifications.user.account.deactivation',
    icon: 'ehealthicon-account_deactivation'
  },
  /** Represents the "Task: Task created or updated" topic type. */
  TASK_UPDATE: {
    titleKey: 'view.patient.detail.notifications.taskupdate',
    icon: 'ehealthicon-shui_opentask'
  }
};

export function getNotificationTitle(topic: string): string {
  const ttDef = notificationTypesConfig[topic];
  return ttDef
    ? intl(ttDef['titleKey'] as LanguageProperty)
    : intl(
        ('view.patient.detail.notifications.custom.' +
          topic) as LanguageProperty,
        topic
      );
}

export function formatDate(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = diffInMs / (1000 * 60);

  if (diffInMinutes < 3) {
    return intl('view.time.rightNow');
  } else if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)} ${intl('view.time.interval.minutes')}`;
  } else if (diffInMinutes >= 60 && diffInMinutes < 120) {
    return `${Math.floor(diffInMinutes / 60)} ${intl(
      'view.time.interval.hour'
    )}`;
  } else if (diffInMinutes >= 120 && diffInMinutes < 24 * 60) {
    return `${Math.floor(diffInMinutes / 60)} ${intl(
      'view.time.interval.hours'
    )}`;
  } else {
    return DefaultDateFormatter.formatDate(date);
  }
}

export function decodeEscapedHtml(escapedHtml: string): string {
  const element = document.createElement('div');
  element.innerHTML = escapedHtml;
  const htmlRes =
    element.childNodes.length == 0 ? '' : element.childNodes[0].nodeValue;
  return htmlRes || '';
}
